import React, { useEffect, useState } from "react";
import { UserData } from "../interface/user.interface";

interface UserTableProps {
  data: UserData[];
  isLoading: boolean;
  onUpdateRole: (id: string, newRole: string) => void;
  onUpdateStatus: (id: string, newStatus: boolean) => void;
}

const UserTable: React.FC<UserTableProps> = ({
  data,
  isLoading,
  onUpdateRole,
  onUpdateStatus,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

  const openModal = (user: UserData) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedUser(null);
    setIsModalOpen(false);
  };
  useEffect(() => {
    if (isModalOpen && selectedUser) {
      const updatedUser = data.find((user) => user.id === selectedUser.id);
      if (updatedUser) {
        setSelectedUser(updatedUser); // Update the selectedUser with the latest data
      }
    }
  }, [data, isModalOpen, selectedUser]);
  return (
    <div className="w-full overflow-hidden shadow-md rounded-lg">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-bgCard text-white">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden sm:table-cell">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Role
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-bgCard text-xs">
            {isLoading
              ? Array.from({ length: 5 }, (_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    </td>
                  </tr>
                ))
              : data.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => openModal(user)}
                    className="hover:bg-gray-50 hover:text-black cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      {user.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{user.role}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isModalOpen && selectedUser && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="relative bg-bgCard rounded-lg p-8 w-full max-w-lg shadow-lg">
            {/* Close Icon */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Close Modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Avatar Section */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                {selectedUser.avatar ? (
                  <img
                    src={selectedUser.avatar}
                    alt={selectedUser.name}
                    className="rounded-full w-28 h-28 border-4 border-gray-300 shadow-lg"
                  />
                ) : (
                  <div className="rounded-full w-28 h-28 border-4 border-gray-300 shadow-lg flex items-center justify-center bg-gray-100 text-black text-5xl text-center">
                    {selectedUser.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <h2 className="text-2xl font-bold mb-1">{selectedUser.name}</h2>
              <p
                className={`text-md font-semibold ${
                  selectedUser.active ? "text-green-600" : "text-red-500"
                } mb-2`}
              >
                {selectedUser.active ? "Active User" : "Inactive User"}
              </p>
            </div>

            {/* Table Section */}
            <div className="mb-6">
              <table className="w-full text-sm border-collapse border border-gray-300">
                <tbody>
                  <tr className="border-b border-gray-300">
                    <td className="px-4 py-2 font-medium">Email</td>
                    <td className="px-4 py-2">{selectedUser.email}</td>
                  </tr>
                  <tr className="border-b border-gray-300">
                    <td className="px-4 py-2 font-medium">Role</td>
                    <td className="px-4 py-2">{selectedUser.role}</td>
                  </tr>
                  <tr className="border-b border-gray-300">
                    <td className="px-4 py-2 font-medium">Status</td>
                    <td
                      className={`px-4 py-2 font-semibold ${
                        selectedUser.active ? "text-green-600" : "text-red-500"
                      }`}
                    >
                      {selectedUser.active ? "Active" : "Inactive"}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300">
                    <td className="px-4 py-2 font-medium">Business Account</td>
                    <td className="px-4 py-2">
                      {selectedUser.isbusiness ? "Yes" : "No"}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Followers</td>
                    <td className="px-4 py-2">
                      {selectedUser.followers.length}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Following</td>
                    <td className="px-4 py-2">
                      {selectedUser.following.length}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-4">
              <button
                onClick={() =>
                  onUpdateStatus(selectedUser.id, !selectedUser.active)
                }
                className={`py-2 px-6 rounded-full shadow-lg text-white my-2 text-sm ${
                  selectedUser.active
                    ? "bg-red-500 hover:bg-red-600 text-white"
                    : "bg-green-500 hover:bg-green-600 text-white"
                }`}
              >
                {selectedUser.active
                  ? "Deactivate Account"
                  : "Activate Account"}
              </button>
              <button
                onClick={() =>
                  onUpdateRole(
                    selectedUser.id,
                    selectedUser.role === "ADMIN" ? "USER" : "ADMIN"
                  )
                }
                className="py-2 px-6 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white my-2 text-sm"
              >
                {`Change Role to ${
                  selectedUser.role === "ADMIN" ? "User" : "Admin"
                }`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTable;
